import { useId, type ReactElement, type ReactNode } from 'react';

export interface FormFieldProps {
  label: string;
  hint?: string;
  /** From problem+json `errors[]`, so validation display is mechanical. */
  error?: string;
  required?: boolean;
  children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean | undefined;
    'aria-required': boolean | undefined;
  }) => ReactNode;
}

/**
 * The one place a label, hint and error are wired to a control. Every control
 * in the app goes through this, so nothing can ship unlabelled.
 */
export function FormField({
  label,
  hint,
  error,
  required,
  children,
}: FormFieldProps): ReactElement {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  // The error replaces the hint rather than stacking, so the description is
  // never ambiguous to a screen reader.
  const describedBy = error !== undefined ? errorId : hint !== undefined ? hintId : undefined;

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {required === true ? (
          <span className="field__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error !== undefined ? true : undefined,
        'aria-required': required === true ? true : undefined,
      })}
      {error !== undefined ? (
        <p className="field__error" id={errorId}>
          {error}
        </p>
      ) : hint !== undefined ? (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
